import { Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative, FacebookDisplayCreative } from '@feathr/blackbox';
import { isAdTagCreative } from '@feathr/blackbox';
import { Alert, Input, Toggle } from '@feathr/components';

import { CreativeDimensionsWell } from '../../../CreativeDimensionsWell';
import AdTagFields from './AdTagFields';
import MetaFields from './MetaFields';

interface ISetupTabProps {
  creative: DisplayCreative;
  /** Campaign ID, used to filter CTA options by campaign objective. */
  campaignId?: string;
}

function SetupTab({ creative, campaignId }: Readonly<ISetupTabProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const isMeta = creative.channel === 'meta';
  const isDisplayBannersnack = !isMeta && creative.isBannersnack;

  return (
    <Stack gap={theme.spacing['4']}>
      <CreativeDimensionsWell creative={creative} />

      <Input
        attribute={'name'}
        label={t('Creative name')}
        model={creative}
        placeholder={t('Enter name')}
        type={'text'}
      />

      {isDisplayBannersnack && (
        <Alert
          description={t(
            'This creative can still be used in campaigns, but can no longer be edited. You can duplicate this creative or create a new image or video creative.',
          )}
          name={'bannersnack-deprecation'}
          title={t('Banner Builder for display ads has been retired')}
          type={'info'}
        />
      )}

      <Toggle
        label={t('Enable creative')}
        layout={'well'}
        onChange={(value) => creative.set({ is_enabled: !!value })}
        value={creative.get('is_enabled')}
      />

      {!isMeta && isAdTagCreative(creative) && <AdTagFields creative={creative} />}

      {isMeta && (
        <MetaFields campaignId={campaignId} creative={creative as FacebookDisplayCreative} />
      )}
    </Stack>
  );
}

export default observer(SetupTab);
